import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "brief-condition-when" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = ["value"]
const _hoisted_4 = { class: "brief-condition-when__daily-rate" }
const _hoisted_5 = {
  key: 0,
  class: "brief-condition-when__daily-rate_infos"
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = {
  key: 1,
  class: "form-information",
  role: "information"
}
const _hoisted_9 = { class: "bold" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { value: "true" }
const _hoisted_12 = { value: "false" }

import {BriefTrackingService} from '@/brief-hub/brief/BriefTrackingService';
    import {useBriefConditions} from '@/brief-hub/brief/useBriefConditions';
    import BriefHubStepLayout from '@/brief-hub/common/ui/layout/BriefHubStepLayout.vue';
    import {DI} from '@/brief-hub/config/DependencyInjection';
    import BudgetTooltip from '@/brief-hub/deposit/conditions/BudgetTooltip.vue';
    import type {ConditionsWhenConstraints} from '@/brief-hub/deposit/conditions/ConditionsWhenConstraints';
    import {TjmRecommendationService} from '@/brief-hub/deposit/conditions/TjmRecommendationService';
    import store from '@/brief-hub/store';
    import {MutationsType} from '@/brief-hub/store/modules/brief/mutations';
    import type {BriefFreelancerProfile, BriefWhenConditions, Payload} from '@/brief-hub/store/modules/brief/types';
    import {FreelancerAvailability, StartDateType, WhenConditionsBriefRequest} from '@/common/api/api-raw-types';
    import ProjectDurationField, {ProjectDurationModel} from '@/common/components/ProjectDurationField.vue';
    import ProjectStartDate, {ProjectStartDateModel} from '@/common/components/start-date/ProjectStartDate.vue';
    import {Vue3MqResource} from '@/common/types/vue3mq';
    import {getPageCountryCode, getPageLocale} from '@malt/dom';
    import {useGlobalProperties} from '@malt/vue3-use';
    import {useVuelidate} from '@vuelidate/core';
    import {maxValue, minValue, required} from '@vuelidate/validators';
    import {ComputedRef, Ref, computed, inject, onMounted, ref, watch} from 'vue';
    import {useRouter} from 'vue-router';
    import {context} from 'vue3-starter/src/typescript/app-context/AppContext';

    type NumericalString = `${number}` | number;

    interface StringSelection {
        label: string;
        value: string;
    }

    interface FreelancerAvailabilityWithLabel {
        label: string;
        value: FreelancerAvailability;
    }

    const MIN_DURATION: number = 1;
    const MAX_DURATION: number = 1000;
    const MAX_FREELANCER_RATE: number = 20000;
    const MIN_BUDGET: number = 1;
    const MAX_BUDGET: number = 9999999;
    
export default /*@__PURE__*/_defineComponent({
  __name: 'BriefConditionsWhenV2',
  setup(__props) {

    const {$features, $msg} = useGlobalProperties();
    const $router = useRouter();
    const mq: Vue3MqResource | undefined = inject<Vue3MqResource>('mq');
    const isMobile: ComputedRef<boolean> = computed(() => !!mq?.mdMinus);

    const tjmRecommendationService = inject<TjmRecommendationService>(DI.tjmRecommendationService);
    const briefTrackingService = inject<BriefTrackingService>(DI.briefTrackingService);

    const brief = ref(store.getters.brief);
    const briefWhenConditions: Ref<BriefWhenConditions> = ref(store.getters.briefWhenConditions);
    const briefFreelancerProfile: Ref<BriefFreelancerProfile> = ref(store.getters.briefFreelancerProfile);

    const isCurrentLocaleES: boolean = getPageCountryCode() === 'ES';
    const durationEstimations: Ref<StringSelection[]> = ref([]);
    const freelancerAvailabilities: Ref<FreelancerAvailabilityWithLabel[]> = ref([]);
    const rateCommunications = ref<StringSelection[]>([]);

    const selectedStartDateField: Ref<ProjectStartDateModel> = ref({
        startDateType: briefWhenConditions.value.startDateType || StartDateType.ASAP,
        startDate: initStartDate() || undefined,
        startDateFlexibility: briefWhenConditions.value.startDateFlexibility || undefined,
    });
    const selectedStartDate: Ref<Date | undefined> = computed(() => selectedStartDateField.value?.startDate);
    const selectedAvailability: Ref<FreelancerAvailability | null> = ref(
        briefWhenConditions.value.freelancerAvailability || FreelancerAvailability.FULL_TIME,
    );
    const selectedDailyFreelancerRate: Ref<NumericalString | null> = ref(briefWhenConditions.value.dailyFreelancerRate);
    const selectedDailyFreelancerRateInt: Ref<number> = computed(() => parseInt(`${selectedDailyFreelancerRate.value}`));
    const selectedBudget: Ref<number | null> = ref(briefWhenConditions.value.budget);
    const selectedCommunicateRate: Ref<string | null> = ref(
        briefWhenConditions.value.communicateRate !== null ? briefWhenConditions.value.communicateRate.toString() : 'false',
    );
    const selectedDurationField: Ref<ProjectDurationModel> = ref({
        value: briefWhenConditions.value?.projectDuration?.value,
        unit: briefWhenConditions.value.projectDuration?.unit,
        fixed: briefWhenConditions.value.projectDuration?.fixed,
    });
    const initialized: Ref<boolean> = ref(false);
    const tjmRecommendation: Ref<number | null> = ref(null);
    const selectedDurationEstimation = computed<string>(() => (moreThanOneMonth.value ? 'SUPERIOR_ONE_MONTH' : 'INFERIOR_ONE_MONTH'));

    const MIN_FREELANCER_RATE: ComputedRef<number> = computed(() => (isCurrentLocaleES ? 100 : 125));
    const constraints: Ref<ConditionsWhenConstraints> = ref({
        MIN_DURATION,
        MAX_DURATION,
        MIN_FREELANCER_RATE: MIN_FREELANCER_RATE.value,
        MAX_FREELANCER_RATE,
        MIN_BUDGET,
        MAX_BUDGET,
    });

    const {isDurationMoreThanOneMonth} = useBriefConditions(brief.value.briefDetails, getPageLocale());
    const moreThanOneMonth = ref<boolean>(false);

    onMounted(async () => {
        await init();
    });

    async function init(): Promise<void> {
        retrieveDurationEstimations();
        retrieveFreelancerAvailabilities();
        retrieveRateCommunications();
        await retrieveTjmRecommendation();
        initialized.value = true;

        briefTrackingService?.triggerBriefFunnelStepEnteredAmplitudeEvent('condition_when', 3, $router.currentRoute.value);
    }

    const tjrRecommendationWithFormatCurrency = computed<string | null>(() => {
        if (!tjmRecommendation.value) {
            return null;
        }
        return Intl.NumberFormat(context.locale, {
            style: 'currency',
            currency: brief.value.briefDetails.currencyCode,
        }).format(tjmRecommendation.value);
    });

    const hasFreelancerDailyRateWarning = computed<boolean>(() => {
        if (!selectedDailyFreelancerRate.value || !tjmRecommendation.value) {
            return false;
        }
        return tjmRecommendation.value - selectedDailyFreelancerRateInt.value > 100;
    });

    function initStartDate(): Date | null {
        if (briefWhenConditions.value.startDate) {
            const date = new Date(briefWhenConditions.value.startDate);
            const currentDate = new Date();
            currentDate.setUTCHours(0, 0, 0, 0);
            return date < currentDate ? null : date;
        }
        return null;
    }

    const currencySymbol = computed<string>(() => {
        return (0)
            .toLocaleString(getPageLocale(), {
                style: 'currency',
                currency: brief.value.briefDetails.currencyCode,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            })
            .replace(/\d/g, '')
            .trim();
    });

    const dailyFreelancerRateErrorMin = computed(() => {
        return selectedDailyFreelancerRateInt.value < constraints.value.MIN_FREELANCER_RATE || false;
    });

    const dailyFreelancerRateErrorMax = computed(() => {
        return selectedDailyFreelancerRateInt.value > constraints.value.MAX_FREELANCER_RATE || false;
    });

    async function retrieveTjmRecommendation(): Promise<void> {
        const category = briefFreelancerProfile.value.selectedCategory;
        const experienceLevel = briefFreelancerProfile.value.selectedExperienceLevel;
        if (category && experienceLevel) {
            const [data, error] = await tjmRecommendationService!.retrieveTjmRecommendation(category, experienceLevel);
            if (!error) {
                tjmRecommendation.value = data;
                if (!selectedDailyFreelancerRate.value) {
                    selectedDailyFreelancerRate.value = data;
                }
            }
        }
    }

    function retrieveDurationEstimations(): void {
        durationEstimations.value = [
            {
                label: $msg('customer.brief.form.when.conditions.project.duration.superior.one.month'),
                value: 'SUPERIOR_ONE_MONTH',
            },
            {
                label: $msg('customer.brief.form.when.conditions.project.duration.inferior.one.month'),
                value: 'INFERIOR_ONE_MONTH',
            },
        ];
    }

    function retrieveFreelancerAvailabilities(): void {
        const labelFor = (frequencyValue: FreelancerAvailability) => $msg(`customer.brief.form.when.conditions.project.frequency.${frequencyValue}`);

        freelancerAvailabilities.value = [
            FreelancerAvailability.FULL_TIME,
            FreelancerAvailability.FOUR_DAYS_PER_WEEK,
            FreelancerAvailability.THREE_DAYS_PER_WEEK,
            FreelancerAvailability.TWO_DAYS_PER_WEEK,
            FreelancerAvailability.ONE_DAY_PER_WEEK,
        ].map((freelancerAvailability) => ({
            label: labelFor(freelancerAvailability),
            value: freelancerAvailability,
        }));
    }

    function retrieveRateCommunications(): void {
        rateCommunications.value = [
            {
                label: $msg('customer.brief.form.when.conditions.project.communicate.tariff.want'),
                value: 'true',
            },
            {
                label: $msg('customer.brief.form.when.conditions.project.communicate.tariff.dont.want'),
                value: 'false',
            },
        ];
    }

    const rules = computed(() => ({
        selectedBudget: {
            ...(!moreThanOneMonth.value
                ? {
                      required,
                      minValueValue: minValue(MIN_BUDGET),
                  }
                : {}),
        },
        selectedDailyFreelancerRate: {
            ...(moreThanOneMonth.value
                ? {
                      required,
                      minValueRef: minValue(MIN_FREELANCER_RATE.value),
                      maxValueValue: maxValue(MAX_FREELANCER_RATE),
                  }
                : {}),
        },
        selectedAvailability: {
            ...(moreThanOneMonth.value
                ? {
                      required,
                  }
                : {}),
        },
        selectedCommunicateRate: {
            ...[moreThanOneMonth.value && required],
        },
    }));

    const v$ = useVuelidate(
        rules,
        {
            selectedStartDateField,
            selectedBudget,
            selectedDailyFreelancerRate,
            selectedCommunicateRate,
            selectedAvailability,
        },
        {$autoDirty: true},
    );

    const isValidateForm: ComputedRef<boolean> = computed(() => {
        return !!(
            !v$.value.$invalid &&
            selectedStartDateField.value &&
            (selectedStartDateField.value.startDateType === StartDateType.CHOSEN_DATE ? selectedStartDateField.value.startDate : true) &&
            selectedDurationField.value?.value &&
            selectedDurationField.value?.value >= MIN_DURATION &&
            selectedDurationField.value?.value <= MAX_DURATION &&
            selectedDurationField.value?.unit &&
            (moreThanOneMonth.value
                ? selectedAvailability.value &&
                  selectedDailyFreelancerRate.value &&
                  parseInt(`${selectedDailyFreelancerRate.value}`) >= MIN_FREELANCER_RATE.value &&
                  parseInt(`${selectedDailyFreelancerRate.value}`) <= MAX_FREELANCER_RATE &&
                  selectedCommunicateRate.value
                : selectedBudget.value && selectedBudget.value >= MIN_BUDGET && selectedBudget.value <= MAX_BUDGET)
        );
    });

    function onFormChange(): void {
        let payload: Payload | null = null;
        if (isValidateForm.value) {
            payload = {
                type: 'WhenConditionsBriefRequest',
                // @ts-ignore
                payload: computePayload(),
            };
        }
        store.commit(MutationsType.UPDATE_PAYLOAD, payload);
        if (initialized.value) {
            store.commit(MutationsType.UPDATE_BRIEF_FORM_WHEN, computePayload());
        }
    }

    watch(
        [
            selectedDurationField,
            selectedStartDateField,
            selectedAvailability,
            selectedBudget,
            selectedDailyFreelancerRate,
            selectedCommunicateRate,
            moreThanOneMonth,
        ],
        onFormChange,
        {immediate: true},
    );

    watch(
        selectedDurationField,
        () => {
            moreThanOneMonth.value = isDurationMoreThanOneMonth(selectedDurationField.value!.value, selectedDurationField.value!.unit);
        },
        {immediate: true},
    );

    function computePayload(): BriefWhenConditions | WhenConditionsBriefRequest {
        return {
            projectDuration: {
                unit: selectedDurationField.value?.unit!,
                value: parseInt(`${selectedDurationField.value?.value}`),
                fixed: selectedDurationField.value?.fixed,
            },
            startDate:
                selectedStartDateField.value?.startDateType === StartDateType.CHOSEN_DATE && selectedStartDate.value
                    ? selectedStartDate.value.toISOString()
                    : null,
            budget: moreThanOneMonth.value ? null : selectedBudget.value,
            dailyFreelancerRate: moreThanOneMonth.value ? selectedDailyFreelancerRateInt.value : null,
            freelancerAvailability: moreThanOneMonth.value ? selectedAvailability.value : null,
            communicateRate: selectedCommunicateRate.value === 'true',
            startDateType: selectedStartDateField.value?.startDateType,
            startDateFlexibility:
                selectedStartDateField.value?.startDateType === 'CHOSEN_DATE' ? selectedStartDateField.value?.startDateFlexibility : null,
        };
    }

return (_ctx: any,_cache: any) => {
  const _component_VJoyLabel = _resolveComponent("VJoyLabel")!
  const _component_VJoyInput = _resolveComponent("VJoyInput")!
  const _component_VJoySelect = _resolveComponent("VJoySelect")!
  const _component_VJoyFormError = _resolveComponent("VJoyFormError")!
  const _component_VJoyIcon = _resolveComponent("VJoyIcon")!
  const _component_VJoyWrapper = _resolveComponent("VJoyWrapper")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(BriefHubStepLayout, null, {
      right: _withCtx(() => [
        _createVNode(BudgetTooltip, {
          dailyFreelancerRate: selectedDailyFreelancerRateInt.value,
          durationEstimation: selectedDurationEstimation.value,
          duration: selectedDurationField.value.value,
          durationUnit: selectedDurationField.value.unit,
          startDate: selectedStartDate.value,
          availability: selectedAvailability.value,
          budget: selectedBudget.value,
          constraints: constraints.value,
          tjmRecommendation: tjmRecommendation.value
        }, null, 8, ["dailyFreelancerRate", "durationEstimation", "duration", "durationUnit", "startDate", "availability", "budget", "constraints", "tjmRecommendation"])
      ]),
      title: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.form.when.conditions.title')), 1)
      ]),
      default: _withCtx(() => [
        _createVNode(_component_VJoyWrapper, {
          direction: "column",
          align: "stretch",
          "no-margin": ""
        }, {
          default: _withCtx(() => [
            _createVNode(ProjectStartDate, {
              "model-value": selectedStartDateField.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = (newValue) => (selectedStartDateField.value = newValue))
            }, null, 8, ["model-value"]),
            _createVNode(ProjectDurationField, {
              "model-value": selectedDurationField.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = (newValue) => (selectedDurationField.value = newValue))
            }, null, 8, ["model-value"]),
            (!moreThanOneMonth.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createVNode(_component_VJoyLabel, { for: "selectedBudget" }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.form.when.conditions.project.budget')), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_VJoyInput, {
                    id: "selectedBudget",
                    "model-value": _unref(v$).selectedBudget.$model,
                    name: "selectedBudget",
                    type: "number",
                    placeholder: "0",
                    min: constraints.value.MIN_BUDGET,
                    max: constraints.value.MAX_BUDGET,
                    suffix: currencySymbol.value,
                    invalid: _unref(v$).selectedBudget.$error,
                    unit: currencySymbol.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = (newBudget) => (selectedBudget.value = parseInt(newBudget)))
                  }, null, 8, ["model-value", "min", "max", "suffix", "invalid", "unit"])
                ]))
              : (_openBlock(), _createBlock(_component_VJoyWrapper, {
                  key: 1,
                  direction: "column",
                  align: "stretch",
                  "no-margin": ""
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", null, [
                      _createVNode(_component_VJoyLabel, { for: "selectedAvailability" }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.form.when.conditions.project.frequency')), 1)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_VJoySelect, {
                        id: "selectedAvailability",
                        modelValue: selectedAvailability.value,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedAvailability).value = $event)),
                        "data-testid": "brief-strategy-availability",
                        required: "",
                        invalid: _unref(v$).selectedAvailability.$invalid
                      }, {
                        default: _withCtx(() => [
                          _cache[6] || (_cache[6] = _createElementVNode("option", { value: "" }, "-", -1)),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(freelancerAvailabilities.value, (availability) => {
                            return (_openBlock(), _createElementBlock("option", {
                              key: availability.value,
                              value: availability.value
                            }, _toDisplayString(availability.label) + " " + _toDisplayString(_unref($msg)('customer.brief.form.when.conditions.project.frequency.by.week')), 9, _hoisted_3))
                          }), 128))
                        ]),
                        _: 1
                      }, 8, ["modelValue", "invalid"])
                    ]),
                    _createVNode(_component_VJoyWrapper, {
                      "no-margin": "",
                      class: "brief-condition-when__daily-rate-wrapper",
                      wrap: isMobile.value ? 'wrap' : 'nowrap',
                      align: isMobile.value ? 'stretch' : 'flex-start'
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_VJoyWrapper, {
                          "no-margin": "",
                          direction: "column",
                          align: "stretch"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_4, [
                              _createVNode(_component_VJoyLabel, { for: "selectedDailyFreelancerRate" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.form.when.conditions.project.tariff.v2')), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_VJoyInput, {
                                id: "selectedDailyFreelancerRate",
                                modelValue: selectedDailyFreelancerRate.value,
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedDailyFreelancerRate).value = $event)),
                                type: "number",
                                name: "input",
                                autocomplete: "off",
                                unit: _unref($msg)('customer.brief.form.when.conditions.project.freelancer.rate.unit.v2', currencySymbol.value),
                                min: constraints.value.MIN_FREELANCER_RATE,
                                max: constraints.value.MAX_FREELANCER_RATE,
                                "data-testid": "daily-freelancer-rate",
                                invalid: _unref(v$).selectedDailyFreelancerRate.$invalid
                              }, null, 8, ["modelValue", "unit", "min", "max", "invalid"])
                            ]),
                            (dailyFreelancerRateErrorMin.value || dailyFreelancerRateErrorMax.value || hasFreelancerDailyRateWarning.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                  (dailyFreelancerRateErrorMin.value || dailyFreelancerRateErrorMax.value)
                                    ? (_openBlock(), _createBlock(_component_VJoyFormError, {
                                        key: 0,
                                        "no-html-error-text": "This is an error"
                                      }, {
                                        default: _withCtx(() => [
                                          _createElementVNode("ul", null, [
                                            (dailyFreelancerRateErrorMin.value)
                                              ? (_openBlock(), _createElementBlock("li", _hoisted_6, _toDisplayString(_unref($msg)('customer.brief.form.when.conditions.project.freelancer.rate.error.min', MIN_FREELANCER_RATE.value)), 1))
                                              : _createCommentVNode("", true),
                                            (dailyFreelancerRateErrorMax.value)
                                              ? (_openBlock(), _createElementBlock("li", _hoisted_7, _toDisplayString(_unref($msg)('customer.brief.form.when.conditions.project.freelancer.rate.error.max', MAX_FREELANCER_RATE)), 1))
                                              : _createCommentVNode("", true)
                                          ])
                                        ]),
                                        _: 1
                                      }))
                                    : _createCommentVNode("", true),
                                  (hasFreelancerDailyRateWarning.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                        _createVNode(_component_VJoyIcon, { name: "info-circle" }),
                                        _createElementVNode("p", null, [
                                          _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.form.when.conditions.project.freelancer.rate.warning')) + " : ", 1),
                                          _createElementVNode("span", _hoisted_9, _toDisplayString(tjrRecommendationWithFormatCurrency.value), 1)
                                        ])
                                      ]))
                                    : _createCommentVNode("", true)
                                ]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }),
                        (moreThanOneMonth.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                              _createVNode(_component_VJoyLabel, { for: "selectedCommunicateRate" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.form.when.conditions.project.communicate.tariff.v3')), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_VJoySelect, {
                                id: "selectedCommunicateRate",
                                modelValue: selectedCommunicateRate.value,
                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedCommunicateRate).value = $event)),
                                required: ""
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("option", _hoisted_11, _toDisplayString(_unref($msg)('general.yes')), 1),
                                  _createElementVNode("option", _hoisted_12, _toDisplayString(_unref($msg)('general.no')), 1)
                                ]),
                                _: 1
                              }, 8, ["modelValue"])
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }, 8, ["wrap", "align"])
                  ]),
                  _: 1
                }))
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})